import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface AircraftType {
  id: number;
  code: string;
  name: string;
  displayName: string;
}

export interface Carrier {
  id: number;
  name: string;
  code: string;
}

export interface FlightStatus {
  id: number;
  name: string;
  color: string;
}

export interface Flight {
  id: number;
  flightNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
  aircraftType: AircraftType;
  carrier: Carrier;
  status: FlightStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFlightRequest {
  flightNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
  aircraftTypeId: number;
  carrierId: number;
  statusId: number;
}

export interface UpdateFlightRequest {
  flightNumber?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  departureTime?: string;
  arrivalTime?: string;
  aircraftTypeId?: number;
  carrierId?: number;
  statusId?: number;
}

export interface FlightSearchRequest {
  flightNumber?: string;
  departureAirport?: string;
  arrivalAirport?: string;
  carrierId?: number;
  aircraftTypeId?: number;
  statusId?: number;
  departureTimeFrom?: string;
  departureTimeTo?: string;
  isActive?: boolean;
}

export interface FlightStats {
  totalFlights: number;
  activeFlights: number;
  completedFlights: number;
  cancelledFlights: number;
  flightsByStatus: Record<string, number>;
  flightsByCarrier: Record<string, number>;
  flightsByAircraftType: Record<string, number>;
  flightsByAirport: Record<string, number>;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class FlightService {
  private baseUrl = '/api/flights';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех рейсов с пагинацией
  async getFlights(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Flight>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение активных рейсов
  async getActiveFlights(): Promise<Flight[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение рейса по ID
  async getFlightById(id: number): Promise<Flight> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение рейса по номеру
  async getFlightByNumber(flightNumber: string): Promise<Flight> {
    const url = getDataApiUrl(`${this.baseUrl}/number/${encodeURIComponent(flightNumber)}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение рейсов по аэропорту
  async getFlightsByAirport(airportCode: string, type = 'all', page = 0, size = 20): Promise<PaginatedResponse<Flight>> {
    const params = new URLSearchParams({
      type,
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/airport/${encodeURIComponent(airportCode)}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение рейсов по дате
  async getFlightsByDate(date: string, airport?: string, page = 0, size = 20): Promise<PaginatedResponse<Flight>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (airport) {
      params.append('airport', airport);
    }
    
    const url = getDataApiUrl(`${this.baseUrl}/date/${date}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание нового рейса
  async createFlight(flightData: CreateFlightRequest): Promise<Flight> {
    const url = getDataApiUrl(this.baseUrl);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(flightData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление рейса
  async updateFlight(id: number, flightData: UpdateFlightRequest): Promise<Flight> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(flightData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление рейса
  async deleteFlight(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Поиск рейсов
  async searchFlights(searchData: FlightSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<Flight>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Изменение статуса рейса
  async updateFlightStatus(id: number, statusId: number): Promise<Flight> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}/status`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ statusId })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение статистики рейсов
  async getFlightStats(dateFrom?: string, dateTo?: string, airport?: string): Promise<FlightStats> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (airport) params.append('airport', airport);
    
    const url = getDataApiUrl(`${this.baseUrl}/stats?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение стандартных рейсов для демонстрации
  getDefaultFlights(): CreateFlightRequest[] {
    return [
      {
        flightNumber: 'KC-123',
        departureAirport: 'ASTANA',
        arrivalAirport: 'ALMATY',
        departureTime: '2025-01-15T10:00:00',
        arrivalTime: '2025-01-15T12:00:00',
        aircraftTypeId: 1,
        carrierId: 1,
        statusId: 1
      },
      {
        flightNumber: 'DV-456',
        departureAirport: 'ALMATY',
        arrivalAirport: 'ASTANA',
        departureTime: '2025-01-15T14:00:00',
        arrivalTime: '2025-01-15T16:00:00',
        aircraftTypeId: 2,
        carrierId: 2,
        statusId: 1
      },
      {
        flightNumber: 'KC-789',
        departureAirport: 'ASTANA',
        arrivalAirport: 'AKTOBE',
        departureTime: '2025-01-15T08:00:00',
        arrivalTime: '2025-01-15T09:30:00',
        aircraftTypeId: 1,
        carrierId: 1,
        statusId: 1
      }
    ];
  }
}

const flightService = new FlightService();
export default flightService; 